import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, LogOut, User as UserIcon, LayoutGrid } from "lucide-react";
import { CATEGORIES } from "@/data/simulations";
import { useAuth } from "@/context/AuthContext";

const ACCENT = "#4FA3D9";
const LOGO = "/adaptience-logo.png";

const initialsOf = (account) => {
  const name = (account?.name || "").trim();
  if (name) {
    const parts = name.split(/\s+/);
    return ((parts[0][0] || "") + (parts[1]?.[0] || "")).toUpperCase();
  }
  const email = account?.email || "";
  return (email[0] || "?").toUpperCase();
};

const SIM_BASE = "/simulations";

// Central, data-driven active-matching for main navigation.
// Default (no `match`) = exact pathname match against `to`.
// Future items can opt into other strategies via a `match` descriptor,
// e.g. Simulations uses { type: "prefix", base: SIM_BASE } to stay active
// across category, individual simulation and preview routes.
const NAV_LINKS = [
  { label: "How It Works", to: "/how-it-works", testid: "nav-how-it-works" },
  { label: "About", to: "/about", testid: "nav-about" },
  { label: "Contact", to: "/contact", testid: "nav-contact" },
];

const isActive = (pathname, to, match) => {
  if (match?.type === "prefix") return pathname === match.base || pathname.startsWith(match.base + "/");
  return pathname === to;
};

export default function Navbar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { account, logout } = useAuth();
  const simActive = isActive(pathname, SIM_BASE, { type: "prefix", base: SIM_BASE });
  const [simOpen, setSimOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSimOpen, setMobileSimOpen] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);
  const closeTimer = useRef(null);
  const navRef = useRef(null);
  const acctRef = useRef(null);

  // Close the account dropdown on outside click.
  useEffect(() => {
    if (!acctOpen) return;
    const onDoc = (e) => { if (acctRef.current && !acctRef.current.contains(e.target)) setAcctOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [acctOpen]);

  const doLogout = async () => {
    setAcctOpen(false);
    setMobileOpen(false);
    navigate("/", { replace: true });
    await logout();
  };

  const canHover = typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia("(hover: hover)").matches
    : true;
  const openSim = () => { if (!canHover) return; clearTimeout(closeTimer.current); setSimOpen(true); };
  const scheduleClose = () => { if (!canHover) return; closeTimer.current = setTimeout(() => setSimOpen(false), 140); };
  const toggleSim = () => { if (canHover) setSimOpen(true); else setSimOpen((v) => !v); };
  const closeAll = () => { setMobileOpen(false); setSimOpen(false); setMobileSimOpen(false); };

  // Shared mobile drawer blocks (reused in both logged-in and logged-out ordering).
  // Not new components/state — just local JSX to avoid duplication when reordering.
  const mobileSimBlock = (
    <>
      <button
        onClick={() => setMobileSimOpen((v) => !v)}
        data-testid="mobile-nav-simulations"
        aria-expanded={mobileSimOpen}
        className="flex w-full items-center justify-between py-3 text-left text-base text-white/90"
        style={simActive ? { color: ACCENT } : undefined}
      >
        Simulations
        <ChevronDown size={18} className={`transition-transform duration-200 ${mobileSimOpen ? "rotate-180" : ""}`} />
      </button>
      {mobileSimOpen && (
        <div className="mb-2 mt-1">
          <Link
            to="/simulations"
            onClick={closeAll}
            data-testid="mobile-explore-all"
            className="block w-full py-2.5 text-left text-base font-medium text-white"
          >
            Explore All Simulations
          </Link>
          <p className="mb-1 mt-4 text-[10px] font-semibold uppercase tracking-[0.22em] text-white/35">Categories</p>
          <div className="ml-1 space-y-0.5 border-l pl-4" style={{ borderColor: "rgba(79,163,217,0.35)" }}>
            {CATEGORIES.map((c) => (
              <Link
                key={c.slug}
                to={`/simulations/${c.slug}`}
                onClick={closeAll}
                data-testid={`mobile-category-${c.slug}`}
                className="block w-full py-2.5 text-left text-sm text-white/60 transition-colors hover:text-white"
                style={isActive(pathname, `/simulations/${c.slug}`) ? { color: ACCENT } : undefined}
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </>
  );

  const mobileNavLinks = NAV_LINKS.map((l) => (
    <Link
      key={l.to}
      to={l.to}
      onClick={closeAll}
      data-testid={`mobile-${l.testid}`}
      className="block w-full border-t border-white/[0.05] py-3.5 text-left text-base text-white/90"
      style={isActive(pathname, l.to, l.match) ? { color: ACCENT } : undefined}
    >
      {l.label}
    </Link>
  ));

  // Lock background scroll while the mobile overlay is open — lock both <body>
  // and the app's actual scroll container (nearest scrollable ancestor of the nav).
  useEffect(() => {
    if (!mobileOpen) return;
    let scroller = navRef.current?.parentElement || null;
    while (scroller && scroller !== document.body) {
      const oy = getComputedStyle(scroller).overflowY;
      if (oy === "auto" || oy === "scroll") break;
      scroller = scroller.parentElement;
    }
    const target = scroller && scroller !== document.body ? scroller : null;
    const bodyPrev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const targetPrev = target ? target.style.overflow : null;
    if (target) target.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = bodyPrev;
      if (target) target.style.overflow = targetPrev;
    };
  }, [mobileOpen]);

  return (
    <nav
      ref={navRef}
      className="sticky top-0 z-50 border-b border-white/[0.06] bg-[#0a0a0a]/70 backdrop-blur-xl"
      data-testid="site-navbar"
    >
      <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-4 sm:px-10">
        <Link to="/" className="flex items-center" data-testid="navbar-logo" aria-label="Adaptience home">
          <img src={LOGO} alt="Adaptience" className="h-6 w-auto sm:h-7" />
          <sup className="ml-0.5 text-[8px] text-white/40">™</sup>
        </Link>

        {/* Desktop nav */}
        <div className="hidden items-center gap-8 text-sm text-white/70 md:flex">
          <div className="relative" onMouseEnter={openSim} onMouseLeave={scheduleClose}>
            <button
              data-testid="nav-simulations"
              onClick={toggleSim}
              aria-expanded={simOpen}
              className="inline-flex items-center gap-1.5 transition-colors hover:text-white"
              style={simActive ? { color: ACCENT } : undefined}
            >
              Simulations
              <ChevronDown size={15} className={`transition-transform duration-200 ${simOpen ? "rotate-180" : ""}`} />
            </button>
            {simOpen && (
              <div
                onMouseEnter={openSim}
                onMouseLeave={scheduleClose}
                data-testid="simulations-dropdown"
                className="absolute right-0 top-full mt-3 w-72 overflow-hidden rounded-2xl border border-white/10 bg-[#0d0d0d] p-2 shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
              >
                <Link
                  to="/simulations"
                  onClick={() => setSimOpen(false)}
                  data-testid="dropdown-explore-all"
                  className="block w-full rounded-lg px-3 py-2 text-left text-sm text-white/90 transition-colors hover:bg-white/[0.06]"
                >
                  Explore All Simulations
                </Link>
                <p className="px-3 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-white/35">Categories</p>
                {CATEGORIES.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/simulations/${c.slug}`}
                    onClick={() => setSimOpen(false)}
                    data-testid={`dropdown-category-${c.slug}`}
                    className="block w-full rounded-lg px-3 py-2 text-left text-sm text-white/70 transition-colors hover:bg-white/[0.06] hover:text-white"
                    style={isActive(pathname, `/simulations/${c.slug}`) ? { color: ACCENT } : undefined}
                  >
                    {c.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
          {NAV_LINKS.map((l) => (
            <Link key={l.to} to={l.to} data-testid={l.testid} className="transition-colors hover:text-white" style={isActive(pathname, l.to, l.match) ? { color: ACCENT } : undefined}>
              {l.label}
            </Link>
          ))}
          {!account ? (
            <Link
              to="/signin"
              data-testid="nav-signin"
              className="text-white/70 transition-colors hover:text-white"
            >
              Sign in
            </Link>
          ) : (
            <div className="relative" ref={acctRef}>
              <button
                onClick={() => setAcctOpen((v) => !v)}
                data-testid="nav-account-avatar"
                aria-label="Account menu"
                aria-expanded={acctOpen}
                className="flex h-9 w-9 items-center justify-center rounded-full text-xs font-semibold text-white transition-transform hover:scale-105"
                style={{ background: ACCENT }}
              >
                {initialsOf(account)}
              </button>
              {acctOpen && (
                <div
                  data-testid="account-dropdown"
                  className="absolute right-0 top-full mt-3 w-64 overflow-hidden rounded-2xl border border-white/10 bg-[#0d0d0d] p-2 shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
                >
                  <div className="px-3 py-2.5">
                    <p className="truncate text-sm font-medium text-white" data-testid="account-menu-name">{account.name || "Adaptience member"}</p>
                    <p className="truncate text-xs text-white/45" data-testid="account-menu-email">{account.email}</p>
                  </div>
                  <div className="my-1 h-px bg-white/[0.06]" />
                  <Link to="/my-adaptience" onClick={() => setAcctOpen(false)} data-testid="account-menu-my-adaptience" className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-white/85 transition-colors hover:bg-white/[0.06]">
                    <LayoutGrid size={15} /> My Adaptience
                  </Link>
                  <Link to="/account/settings" onClick={() => setAcctOpen(false)} data-testid="account-menu-settings" className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-white/85 transition-colors hover:bg-white/[0.06]">
                    <UserIcon size={15} /> Account settings
                  </Link>
                  <button onClick={doLogout} data-testid="account-menu-signout" className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm text-white/85 transition-colors hover:bg-white/[0.06]">
                    <LogOut size={15} /> Sign out
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mobile toggle */}
        <button
          className="text-white/80 md:hidden"
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Menu"
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile overlay — portaled to <body> so backdrop-filter on <nav> doesn't
          trap position:fixed; fills below the header and does not push page content */}
      {mobileOpen && createPortal(
        <div
          className="fixed inset-x-0 bottom-0 top-[57px] z-40 overflow-y-auto overscroll-contain bg-[#0a0a0a] px-6 py-4 md:hidden"
          data-testid="mobile-menu"
        >
          {!account ? (
            <>
              {mobileSimBlock}
              {mobileNavLinks}
              <Link
                to="/signin"
                onClick={closeAll}
                data-testid="mobile-nav-signin"
                className="block w-full border-t border-white/[0.05] py-3.5 text-left text-base text-white/90"
              >
                Sign in
              </Link>
            </>
          ) : (
            <>
              {/* Account identity — top of the signed-in drawer */}
              <div className="mb-2 flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full text-xs font-semibold text-white" style={{ background: ACCENT }}>
                  {initialsOf(account)}
                </span>
                <div>
                  <p className="text-sm font-medium text-white" data-testid="mobile-account-name">{account.name || "Adaptience member"}</p>
                  <p className="text-xs text-white/45" data-testid="mobile-account-email">{account.email}</p>
                </div>
              </div>
              <Link to="/my-adaptience" onClick={closeAll} data-testid="mobile-account-my-adaptience" className="block w-full py-3 text-left text-base text-white/90">My Adaptience</Link>
              <Link to="/account/settings" onClick={closeAll} data-testid="mobile-account-settings" className="block w-full py-3 text-left text-base text-white/90">Account settings</Link>

              {/* Divider, then Simulations toggle + panel */}
              <div className="border-t border-white/[0.05]">
                {mobileSimBlock}
              </div>
              {mobileNavLinks}

              {/* Divider, then Sign out at the bottom */}
              <div className="border-t border-white/[0.05]">
                <button onClick={doLogout} data-testid="mobile-account-signout" className="block w-full py-3 text-left text-base text-white/90">Sign out</button>
              </div>
            </>
          )}
        </div>,
        document.body
      )}
    </nav>
  );
}
